// Вертикальная схема компоновки — таблица «иконка ↔ метаданные», по
// одной строке на секцию. Сверху вниз = устье → забой.
//
// Каждая строка — отдельный SVG фиксированной высоты (НЕ × quantity).
// Если бы умножали на qty, DP×40 раздул бы колонку до 3000+ px.
// Quantity показывается лейблом справа. Длинные трубы (DP/DC/HWDP/Casing/
// PDM) рендерятся с «разрывом» посередине — зигзагом, как на классических
// чертежах компоновок: «здесь обрыв, длина больше изображённой».
//
// Ширина каждой иконки пропорциональна её OD относительно макс OD в
// компоновке (DC > DP, casing толще DP — реальные пропорции). Высота —
// фиксированная по типу.
//
// Стили (`.steel`, `.body`, `.dark`, `.accent-fill`, `.cone`, `.cutters`,
// `.hollow`, `.line`) переиспользуются с горизонтальных glyph-иконок
// (см. ToolPage.tsx и components.css).

import { Show, For, createMemo } from 'solid-js'
import type { Component, JSX } from 'solid-js'
import { KIND_BY_ID, fromSi, bodyOuterDiamM, bodyInnerDiamM, unwrapPayload } from './tool_kinds'
import type { ToolDto } from './api/tool'
import type { ToolKind } from './tool_kind'

// ────── входные секции (из редактора компоновки) ──────

export type SchematicSection = {
  uid:      number
  tool_id:  string | null
  quantity: number
}

export type DrillstringSchematicProps = {
  sections: SchematicSection[]
  toolById: Record<string, ToolDto>
}

// ────── размеры/масштаб ──────

const ICON_W = 90        // полная ширина SVG иконки в px (= viewBox units)
const PAD_X  = 8
const W_MAX  = ICON_W - PAD_X * 2  // = 74 — макс ширина инструмента
const CX     = ICON_W / 2

// Иконическая высота секции в px — НЕ умножается на quantity. Длина
// конкретного DP (9 / 10 / 12 м) не отражается; цель — узнаваемый силуэт.
// `Partial`: для не-перечисленных kind берётся дефолт (60px) в `resolveSection`.
const ICON_H: Partial<Record<ToolKind, number>> = {
  pdc_bit:     64,
  tricone_bit: 64,
  dp:          82,
  dc:          90,
  hmdc:        90,
  hwdp1:       82,
  hwdp2:       82,
  casing:      96,
  stabilizer:  58,
  reamer:      62,
  pdm:         110,
  tj:          24,
  dv_collar:   40,
  packer:      90,
}

// Типы, у которых рисуем «разрыв» посередине (длинные трубы / двигатели).
// Долота, локи, стабилизаторы — короткие, разрыв не нужен.
const HAS_BREAK: ReadonlySet<ToolKind> = new Set<ToolKind>([
  'dp', 'dc', 'hmdc', 'hwdp1', 'hwdp2', 'casing', 'pdm', 'packer',
])

// ────── разрешение tool_id → схематический контекст ──────

type Resolved = {
  uid:      number
  kindId:   ToolKind
  outerD:   number  // mm; 0 если у kind нет dim
  innerD:   number  // mm; 0 если непустотелый
  quantity: number
  height:   number  // px иконки (без × qty)
  title:    string
  payload:  Record<string, unknown>
}

function resolveSection(
  s: SchematicSection,
  toolById: Record<string, ToolDto>,
): Resolved | null {
  if (s.tool_id == null) return null
  const t = toolById[s.tool_id]
  if (!t) return null
  // body_outer/inner возвращают метры → переводим в мм для рендера.
  const outerD = fromSi(bodyOuterDiamM(t.kind, t.payload), 'mm')
  const innerD = fromSi(bodyInnerDiamM(t.kind, t.payload), 'mm')
  const unit   = ICON_H[t.kind] ?? 60
  const qty    = Math.max(1, s.quantity)
  return {
    uid:      s.uid,
    kindId:   t.kind,
    outerD,
    innerD,
    quantity: qty,
    height:   unit,
    title:    t.title,
    payload:  t.payload,
  }
}

// ────── отрисовка одной секции ──────

type Geom = {
  outerW: number   // ширина тела в px
  innerW: number   // ширина бора в px (0 если непустотелый)
  height: number   // высота секции в px
}

function renderKind(kindId: ToolKind, g: Geom): JSX.Element {
  const { outerW, innerW, height: H } = g
  const left  = CX - outerW / 2
  const right = CX + outerW / 2
  const innerLeft = CX - innerW / 2

  switch (kindId) {
    // ── DP: труба с двумя замками-уплотнениями (вверху и внизу) ──
    case 'dp': {
      const tjW = Math.min(outerW + 6, W_MAX)
      const tjL = CX - tjW / 2
      const TJH = 9
      return (
        <>
          <rect class="dark" x={tjL} y={0} width={tjW} height={TJH} rx={2} />
          <rect class="body" x={left} y={TJH} width={outerW} height={H - 2 * TJH} />
          <Show when={innerW > 0}>
            <rect class="hollow" x={innerLeft} y={TJH} width={innerW} height={H - 2 * TJH} />
          </Show>
          <rect class="dark" x={tjL} y={H - TJH} width={tjW} height={TJH} rx={2} />
        </>
      )
    }

    // ── DC / HMDC: толстая сплошная труба с разрезом ──
    case 'dc':
    case 'hmdc': {
      return (
        <>
          <rect class="body" x={left} y={0} width={outerW} height={H} />
          <rect class="hollow" x={innerLeft} y={0} width={innerW} height={H} />
          <Show when={kindId === 'hmdc'}>
            <line class="line" x1={left + 4} x2={left + 4} y1={4} y2={H - 4} />
            <line class="line" x1={right - 4} x2={right - 4} y1={4} y2={H - 4} />
          </Show>
        </>
      )
    }

    // ── HWDP: труба с замками; HWDP2 — с центральным wear-pad ──
    case 'hwdp1':
    case 'hwdp2': {
      const tjW = Math.min(outerW + 4, W_MAX)
      const tjL = CX - tjW / 2
      const TJH = 8
      const padH = 18
      const padY = (H - padH) / 2
      return (
        <>
          <rect class="dark" x={tjL} y={0} width={tjW} height={TJH} rx={2} />
          <rect class="steel" x={left} y={TJH} width={outerW} height={H - 2 * TJH} />
          <Show when={innerW > 0}>
            <rect class="hollow" x={innerLeft} y={TJH} width={innerW} height={H - 2 * TJH} />
          </Show>
          <Show when={kindId === 'hwdp2'}>
            <rect class="dark" x={tjL} y={padY} width={tjW} height={padH} rx={2} />
          </Show>
          <rect class="dark" x={tjL} y={H - TJH} width={tjW} height={TJH} rx={2} />
        </>
      )
    }

    // ── Casing: толстая обсадная труба, муфта внизу ──
    case 'casing': {
      const collH = 12
      return (
        <>
          <rect class="steel" x={left} y={0} width={outerW} height={H} />
          <rect class="hollow" x={innerLeft} y={0} width={innerW} height={H} />
          <rect class="dark" x={left - 2} y={H - collH} width={outerW + 4} height={collH} rx={2} />
        </>
      )
    }

    // ── PDC: pin сверху, конус-тело, режущая часть с резцами внизу ──
    case 'pdc_bit': {
      const pinW = Math.max(20, outerW * 0.45)
      const pinH = 12
      const pinL = CX - pinW / 2
      const pinR = CX + pinW / 2
      const cuttersH = 14
      return (
        <>
          <rect class="dark" x={pinL} y={0} width={pinW} height={pinH} rx={2} />
          <path
            class="line"
            d={`M${pinL + 3} 4 H${pinR - 3} M${pinL + 3} 7 H${pinR - 3} M${pinL + 3} 10 H${pinR - 3}`}
          />
          <path
            class="body"
            d={`M${pinL} ${pinH} L${left} ${H - cuttersH} L${right} ${H - cuttersH} L${pinR} ${pinH} Z`}
          />
          <path
            class="cutters"
            d={`M${left} ${H - cuttersH} L${right} ${H - cuttersH} L${right - 4} ${H} L${left + 4} ${H} Z`}
          />
          <circle class="accent-fill" cx={CX} cy={H - cuttersH / 2} r={3} />
        </>
      )
    }

    // ── Tricone: pin сверху, тело, три шарошки внизу ──
    case 'tricone_bit': {
      const pinW = Math.max(20, outerW * 0.45)
      const pinH = 12
      const pinL = CX - pinW / 2
      const pinR = CX + pinW / 2
      const coneR = Math.max(5, Math.min(8, outerW / 6))
      return (
        <>
          <rect class="dark" x={pinL} y={0} width={pinW} height={pinH} rx={2} />
          <path
            class="body"
            d={`M${pinL} ${pinH} L${left} ${H - 2 * coneR} L${right} ${H - 2 * coneR} L${pinR} ${pinH} Z`}
          />
          <circle class="cone" cx={left + coneR} cy={H - coneR} r={coneR} />
          <circle class="cone" cx={right - coneR} cy={H - coneR} r={coneR} />
          <circle class="cone" cx={CX} cy={H - coneR + coneR / 2} r={coneR} />
        </>
      )
    }

    // ── Stabilizer / Reamer: труба с лопастями (3 ребра) ──
    case 'stabilizer':
    case 'reamer': {
      const bodyW = Math.max(outerW * 0.55, 20)
      const bodyL = CX - bodyW / 2
      const taper = 8
      return (
        <>
          <rect class="steel" x={bodyL} y={0} width={bodyW} height={H} />
          <path
            class="accent-fill"
            d={`M${bodyL} ${taper} L${left} ${taper + 4} L${left} ${H - taper - 4} L${bodyL} ${H - taper} Z`}
          />
          <path
            class="accent-fill"
            d={`M${bodyL + bodyW} ${taper} L${right} ${taper + 4} L${right} ${H - taper - 4} L${bodyL + bodyW} ${H - taper} Z`}
          />
          <Show when={kindId === 'reamer'}>
            <rect class="cutters" x={left} y={H / 2 - 6} width={outerW} height={12} />
          </Show>
        </>
      )
    }

    // ── PDM (винтовой забойный двигатель) ──
    case 'pdm': {
      const motorW = Math.min(40, W_MAX)
      const motorL = CX - motorW / 2
      const pipeW  = 22
      const pipeL  = CX - pipeW / 2
      const PIN    = 9
      const TAPER  = 9
      const motorH = H - PIN * 2 - TAPER * 2
      return (
        <>
          <rect class="dark" x={pipeL} y={0} width={pipeW} height={PIN} rx={2} />
          <path
            class="body"
            d={`M${pipeL} ${PIN} L${motorL} ${PIN + TAPER} L${motorL + motorW} ${PIN + TAPER} L${pipeL + pipeW} ${PIN} Z`}
          />
          <rect class="body" x={motorL} y={PIN + TAPER} width={motorW} height={motorH} />
          <rect class="hollow" x={CX - 4} y={PIN + TAPER} width={8} height={motorH} />
          <path
            class="body"
            d={`M${motorL} ${PIN + TAPER + motorH} L${pipeL} ${H - PIN} L${pipeL + pipeW} ${H - PIN} L${motorL + motorW} ${PIN + TAPER + motorH} Z`}
          />
          <rect class="dark" x={pipeL} y={H - PIN} width={pipeW} height={PIN} rx={2} />
        </>
      )
    }

    // ── TJ — короткий резьбовой блок ──
    case 'tj': {
      const w = 36
      const l = CX - w / 2
      return (
        <>
          <rect class="steel" x={l} y={0} width={w} height={H} />
          <rect class="dark" x={l + 2} y={2} width={w - 4} height={H - 4} rx={1} />
          <For each={[0.25, 0.5, 0.75]}>
            {(p) => (
              <line
                class="line"
                x1={l + 4}
                x2={l + w - 4}
                y1={2 + p * (H - 4)}
                y2={2 + p * (H - 4)}
              />
            )}
          </For>
        </>
      )
    }

    default: {
      return (
        <>
          <rect class="body" x={left} y={0} width={outerW} height={H} />
          <Show when={innerW > 0}>
            <rect class="hollow" x={innerLeft} y={0} width={innerW} height={H} />
          </Show>
        </>
      )
    }
  }
}

// ────── overlay-разрыв («это длиннее, чем нарисовано») ──────
// Рисуем ПОВЕРХ тела: полоса фоновым цветом + два зигзага. Эффект как на
// классических чертежах труб — обрыв с zigzag-маркером.
//
// HWDP2 — у него wear-pad ровно в центре; чтобы разрыв не наехал на pad,
// сдвигаем его в верхнюю четверть. Для остальных труб — стандартное H/2.
function renderBreak(g: Geom, kindId: ToolKind): JSX.Element {
  const { outerW, height: H } = g
  if (outerW <= 0) return null
  const left  = CX - outerW / 2
  const right = CX + outerW / 2
  const yMid  = kindId === 'hwdp2' ? H * 0.28 : H / 2
  const band  = 9                  // вертикальная полоса разрыва
  const bL = left - 1
  const bR = right + 1
  const zigUp   = `M${bL} ${yMid - 2} L${bR} ${yMid + 0.5}`
  const zigDown = `M${bL} ${yMid + 1} L${bR} ${yMid + 3.5}`
  return (
    <g class="schematic-break">
      <rect class="schematic-break-cut" x={bL} y={yMid - band / 2} width={bR - bL} height={band} />
      <path class="schematic-break-line" d={zigUp} />
      <path class="schematic-break-line" d={zigDown} />
    </g>
  )
}

// ────── формат метаданных (правый столбец) ──────

function formatDims(r: Resolved): string {
  const kind = KIND_BY_ID[r.kindId]
  if (!kind || kind.fields.length === 0) return ''
  const body = unwrapPayload(r.kindId, r.payload)
  if (!body) return ''
  // Берём «информативные» поля — диаметральные. Длину выкидываем (она в
  // иконке подразумевается), оставляем диаметральные. Если все были про
  // длину — ладно, вернём их.
  const dimFields = kind.fields.filter((f) => f.unit === 'mm')
  const list = dimFields.length > 0 ? dimFields : kind.fields.slice(0, 3)
  return list
    .map((f) => {
      const raw = body[f.key]
      const num = typeof raw === 'number' ? raw : 0
      return `${f.label}=${fromSi(num, f.unit).toFixed(f.unit === 'mm' ? 1 : 3)}`
    })
    .join(' · ')
}

// ────── главный компонент ──────

export const DrillstringSchematic: Component<DrillstringSchematicProps> = (props) => {
  const resolved = createMemo<Resolved[]>(() => {
    const out: Resolved[] = []
    for (const s of props.sections) {
      const r = resolveSection(s, props.toolById)
      if (r) out.push(r)
    }
    return out
  })

  // Макс OD по компоновке — для пропорциональной ширины. Если ни у кого
  // нет OD (только tj), берём 1, чтобы избежать деления на 0 —
  // ширины таких секций фиксированы внутри renderKind.
  const maxOD = createMemo<number>(() => {
    let m = 0
    for (const r of resolved()) if (r.outerD > m) m = r.outerD
    return m > 0 ? m : 1
  })

  const widthOf = (r: Resolved): number =>
    r.outerD > 0 ? W_MAX * (r.outerD / maxOD()) : 0

  const innerOfSec = (r: Resolved): number =>
    r.innerD > 0 ? W_MAX * (r.innerD / maxOD()) : 0

  return (
    <Show
      when={resolved().length > 0}
      fallback={
        <div class="schematic-empty">
          <span class="muted text-xs">Добавьте секции — здесь появится схема</span>
        </div>
      }
    >
      <div class="schematic-table">
        <For each={resolved()}>
          {(r) => {
            const geom: Geom = {
              outerW: widthOf(r),
              innerW: innerOfSec(r),
              height: r.height,
            }
            const kind = KIND_BY_ID[r.kindId]
            return (
              <div class="schematic-row">
                <svg
                  class="schematic-cell"
                  width={ICON_W}
                  height={r.height}
                  viewBox={`0 0 ${ICON_W} ${r.height}`}
                  role="img"
                  aria-label={r.title}
                >
                  {renderKind(r.kindId, geom)}
                  <Show when={HAS_BREAK.has(r.kindId)}>
                    {renderBreak(geom, r.kindId)}
                  </Show>
                </svg>
                <div class="schematic-meta">
                  <div class="schematic-meta-head">
                    <span class="schematic-meta-code">{kind?.code ?? r.kindId}</span>
                    <Show when={r.quantity > 1}>
                      <span class="schematic-meta-qty mono">×{r.quantity}</span>
                    </Show>
                  </div>
                  <Show when={formatDims(r).length > 0}>
                    <div class="schematic-meta-dims mono">{formatDims(r)}</div>
                  </Show>
                </div>
              </div>
            )
          }}
        </For>
      </div>
    </Show>
  )
}
